import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { API, LOGO_URL } from "@/constants";
import Seo from "@/components/Seo";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const inputCls =
  "w-full rounded-lg border border-kera-line bg-white px-4 py-2.5 text-sm text-kera-ink outline-none focus:border-kera-gold";

const COUNTRIES = [
  "Ireland", "United Kingdom", "Germany", "France", "Spain", "Portugal", "Italy", "Netherlands",
  "Belgium", "Luxembourg", "Austria", "Poland", "Czechia", "Slovakia", "Slovenia", "Croatia",
  "Hungary", "Romania", "Bulgaria", "Greece", "Cyprus", "Malta", "Denmark", "Sweden", "Finland",
  "Norway", "Iceland", "Switzerland", "Estonia", "Latvia", "Lithuania",
];

const BUSINESS_TYPES = ["Retail", "Online", "Restaurant", "Wholesale / Distribution"];

const EMPTY_JOIN = {
  name: "", business_type: "", company: "", country: "", vat_id: "",
  phone: "", address: "", postcode: "", email: "", password: "", message: "", agree: false,
};

export default function TradePage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("join");
  const [form, setForm] = useState(EMPTY_JOIN);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState("");

  if (user && tab === "login") {
    navigate(user.role === "trader" ? "/trade/dashboard" : "/admin", { replace: true });
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitJoin = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const { agree, ...payload } = form;
      await axios.post(`${API}/trade/apply`, payload);
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

    const submitLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      const data = await login(loginForm.email, loginForm.password);
      navigate(data.role === "trader" ? "/trade/dashboard" : "/admin", { replace: true });
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setForgotError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  const labelCls = "block font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1.5";

  return (
    <main className="min-h-screen bg-black text-white font-body kera-grain" data-testid="trade-page">
      <Seo title="Trade & Wholesale" description="Join the KERA trader programme — wholesale pricing in EUR, branch-based ordering and delivery planning for stockists and restaurants across Europe." path="/trade" />
      <div className="px-4 sm:px-8 max-w-2xl mx-auto pt-16 pb-24">
        <div className="text-center mb-10">
          <Link to="/">
            <img src={LOGO_URL} alt="KERA" className="h-12 w-auto mx-auto" data-testid="trade-logo" />
          </Link>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-kera-gold">
            Trader Programme
          </p>
          <h1 className="mt-3 font-display font-bold text-3xl sm:text-4xl">Grow with KERA.</h1>
          <p className="mt-3 text-sm text-white/60">Global flavours, local homes — stock God's Own Country in your store.</p>
        </div>

        <div className="flex gap-2 justify-center mb-8" data-testid="trade-tabs">
          {[["join", "Become a Partner"], ["login", "Trade Login"]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`trade-tab-${key}`}
              className={`rounded-full px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border transition-colors ${
                tab === key ? "bg-kera-gold text-black border-kera-gold" : "border-white/25 text-white/70 hover:border-kera-gold"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "join" &&
          (sent ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-kera-gold/40 bg-white/5 p-10 text-center" data-testid="trade-join-success">
              <CheckCircle2 className="mx-auto text-kera-gold" size={40} strokeWidth={1.5} />
              <h2 className="mt-5 font-display font-semibold text-2xl">Application received</h2>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">
                Our distribution team will review your trade account application and email you once approved — then sign in with your email and portal password to see wholesale pricing and place orders.
              </p>
              <Link to="/products" className="mt-7 inline-block rounded-full bg-kera-gold text-black px-7 py-3 text-sm font-semibold hover:bg-kera-goldlight transition-colors" data-testid="trade-success-browse">
                Browse the range
              </Link>
            </motion.div>
          ) : (
            <form onSubmit={submitJoin} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-7 sm:p-8" data-testid="trade-join-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Full Name *</label>
                  <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="trade-field-name" />
                </div>
                <div>
                  <label className={labelCls}>Preferred Type of Business *</label>
                  <select required value={form.business_type} onChange={(e) => set("business_type", e.target.value)} className={inputCls} data-testid="trade-field-business-type">
                    <option value="">Select…</option>
                    {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Company Name *</label>
                  <input required value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} data-testid="trade-field-company" />
                </div>
                <div>
                  <label className={labelCls}>Country *</label>
                  <select required value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} data-testid="trade-field-country">
                    <option value="">Select…</option>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>VAT ID *</label>
                  <input required value={form.vat_id} onChange={(e) => set("vat_id", e.target.value)} className={inputCls} placeholder="IE1234567A" data-testid="trade-field-vat" />
                </div>
                <div>
                  <label className={labelCls}>Phone *</label>
                  <input required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="trade-field-phone" />
                </div>
                <div>
                  <label className={labelCls}>Address *</label>
                  <input required value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} data-testid="trade-field-address" />
                </div>
                <div>
                  <label className={labelCls}>Postcode *</label>
                  <input required value={form.postcode} onChange={(e) => set("postcode", e.target.value)} className={inputCls} data-testid="trade-field-postcode" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Email *</label>
                  <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="trade-field-email" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Portal Password * (min 8 characters — for your trader login)</label>
                  <input required type="password" minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} className={inputCls} data-testid="trade-field-password" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Message</label>
                  <textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Store locations, products of interest, timelines…" className={inputCls} data-testid="trade-field-message" />
                </div>
              </div>
              <label className="mt-5 flex items-start gap-3 text-xs text-white/60 leading-relaxed cursor-pointer">
                <input required type="checkbox" checked={form.agree} onChange={(e) => set("agree", e.target.checked)} className="accent-[#C5A059] h-4 w-4 mt-0.5" data-testid="trade-field-agree" />
                I agree that KERA Foods may use the information provided to evaluate my trade account application and contact me regarding this enquiry.
              </label>
              {error && <p className="mt-4 text-sm text-red-400" data-testid="trade-join-error">{error}</p>}
              <button type="submit" disabled={sending} className="mt-6 w-full rounded-full bg-kera-gold text-black py-3.5 text-sm font-semibold tracking-wide hover:bg-kera-goldlight transition-colors disabled:opacity-50" data-testid="trade-join-submit">
                {sending ? "Sending…" : "Apply for Trade Account"}
              </button>
            </form>
          ))}

        {tab === "login" && !forgotOpen && (
          <form onSubmit={submitLogin} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-7 sm:p-8 max-w-md mx-auto" data-testid="trade-login-form">
            <label className={labelCls}>Email</label>
            <input type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className={`${inputCls} mb-5`} data-testid="trade-login-email" />
            <label className={labelCls}>Password</label>
            <input type="password" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className={inputCls} data-testid="trade-login-password" />
            {loginError && <p className="mt-4 text-sm text-red-400" data-testid="trade-login-error">{loginError}</p>}
            <button type="submit" disabled={loginLoading} className="mt-6 w-full rounded-full bg-kera-gold text-black py-3.5 text-sm font-semibold tracking-wide hover:bg-kera-goldlight transition-colors disabled:opacity-50" data-testid="trade-login-submit">
              {loginLoading ? "Signing in…" : "Sign In"}
            </button>
            <p className="mt-5 text-center">
              <button type="button" onClick={() => { setForgotOpen(true); setForgotSent(false); setForgotEmail(loginForm.email); }} className="text-xs text-white/50 hover:text-kera-gold transition-colors underline underline-offset-4" data-testid="forgot-password-link">
                Forgot your password?
              </button>
            </p>
          </form>
        )}

        {tab === "login" && forgotOpen && (
          <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-7 sm:p-8 max-w-md mx-auto" data-testid="forgot-password-panel">
            {forgotSent ? (
              <div className="text-center" data-testid="forgot-password-success">
                <CheckCircle2 className="mx-auto text-kera-gold" size={36} strokeWidth={1.5} />
                <h2 className="mt-4 font-display font-semibold text-xl">Check your inbox</h2>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">
                  If an account exists for <span className="text-kera-gold">{forgotEmail}</span>, we've emailed a password reset link. It stays valid for 1 hour.
                </p>
                <button onClick={() => setForgotOpen(false)} className="mt-6 rounded-full border border-white/25 px-6 py-2.5 text-sm hover:border-kera-gold hover:text-kera-gold transition-colors" data-testid="forgot-password-back">
                  Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={submitForgot} data-testid="forgot-password-form">
                <h2 className="font-display font-semibold text-xl mb-2">Reset your password</h2>
                <p className="text-sm text-white/60 mb-6">Enter your trade account email and we'll send you a reset link.</p>
                <label className={labelCls}>Email</label>
                <input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className={inputCls} data-testid="forgot-password-email" />
                {forgotError && <p className="mt-4 text-sm text-red-400" data-testid="forgot-password-error">{forgotError}</p>}
                <button type="submit" disabled={forgotLoading} className="mt-6 w-full rounded-full bg-kera-gold text-black py-3.5 text-sm font-semibold tracking-wide hover:bg-kera-goldlight transition-colors disabled:opacity-50" data-testid="forgot-password-submit">
                  {forgotLoading ? "Sending…" : "Email me a reset link"}
                </button>
                <p className="mt-5 text-center">
                  <button type="button" onClick={() => setForgotOpen(false)} className="text-xs text-white/50 hover:text-kera-gold transition-colors underline underline-offset-4" data-testid="forgot-password-cancel">
                    Back to sign in
                  </button>
                </p>
              </form>
            )}
          </div>
        )}

        <p className="text-center mt-8">
          <Link to="/" className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-kera-gold transition-colors" data-testid="trade-back">
            ← Back to site
          </Link>
        </p>
      </div>
    </main>
  );
}
